/**
 * PersonaWelcome — 페르소나별 환영 메시지 + 스타터 칩 컴포넌트 (Wave 4B)
 *
 * 현재 페르소나의 welcomeMessage + starterQuestions 4개를 칩으로 표시합니다.
 * 칩 클릭 시 onStarterClick(question) 콜백을 호출합니다.
 */

import { Sparkles, User } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { PersonaDefinition } from "@modootoday/core-agent/persona";

export interface PersonaWelcomeProps {
  /** 현재 페르소나 (null이면 렌더링 안 함) */
  persona: PersonaDefinition | null | undefined;
  /** 스타터 칩 클릭 시 호출 */
  onStarterClick: (question: string) => void;
  /** 로딩 상태 */
  isLoading?: boolean;
  className?: string;
}

export function PersonaWelcome({
  persona,
  onStarterClick,
  isLoading = false,
  className,
}: PersonaWelcomeProps) {
  if (!persona && !isLoading) return null;

  return (
    <div className={cn("w-full", className)}>
      {/* 페르소나 환영 메시지 */}
      {persona && (
        <div className="mb-6 rounded-xl border border-accent/20 bg-accent/5 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent/15">
              <User className="size-4 text-text-brand" />
            </div>
            <div>
              <p className="text-[13px] font-medium text-text-primary">
                {persona.name}을(를) 위한 맞춤 안내
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-text-tertiary">
                {persona.welcomeMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 페르소나별 스타터 칩 */}
      {persona && persona.starterQuestions.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-text-tertiary">
            <Sparkles className="size-3" />
            <span>추천 질문</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {persona.starterQuestions.map((question, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onStarterClick(question)}
                className={cn(
                  "rounded-xl border border-stroke-subtle bg-surface-raised px-4 py-3 text-left",
                  "text-[12px] font-medium text-text-primary",
                  "hover:bg-surface-secondary hover:border-accent/30 transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
                )}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-xl border border-stroke-subtle bg-surface-sunken"
            />
          ))}
        </div>
      )}
    </div>
  );
}
